import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";

const BookDemoPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    countryCode: "+971",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
  });

  const [submissionStatus, setSubmissionStatus] = useState({
    loading: false,
    message: "",
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    } else {
      newErrors.name = "";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
      isValid = false;
    } else {
      newErrors.company = "";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    } else {
      newErrors.email = "";
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setSubmissionStatus({ loading: true, message: "Sending your request..." });

        // Create FormData object for web3forms
        const formDataObj = new FormData();

        // Add form fields to FormData
        formDataObj.append("name", formData.name);
        formDataObj.append("company", formData.company);
        formDataObj.append("email", formData.email);
        formDataObj.append("phone", `${formData.countryCode} ${formData.phone}`);
        formDataObj.append("message", formData.message);

        // Add access key for web3forms
        formDataObj.append("access_key", "3538f588-ad0a-41ee-8716-864a36b287df");

        // Submit form to web3forms
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          body: formDataObj,
        });

        const data = await response.json();

        if (data.success) {
          setSubmissionStatus({ loading: false, message: "Form submitted successfully!" });
          // Redirect to thank-you page after successful submission
          setTimeout(() => {
            navigate("/thank-you");
          }, 1000);
        } else {
          console.error("Form submission error:", data);
          setSubmissionStatus({ loading: false, message: data.message || "Something went wrong. Please try again." });
        }
      } catch (error) {
        console.error("Form submission error:", error);
        setSubmissionStatus({ loading: false, message: "An unexpected error occurred. Please try again." });
      }
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <ScrollReveal className="hero-content" direction="up" delay={200}>
            <h1 className="hero-title">Request Your Free Demo</h1>
            <p className="hero-description">
              Experience how spiderSilk transforms your risk detection and response. Fill out the form and we'll handle
              the rest.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <main className="main">
        <div className="main__curved curved-ellipse"></div>
        <div className="main__curved-line curved-ellipse"></div>

        {/* Book-demo Section */}
        <section className="book-demo">
          <div className="book-demo-container container">
            <ScrollReveal direction="left" delay={300}>
              <form className="book-demo-form" onSubmit={handleSubmit}>
                {/* Submission Status Message */}
                {submissionStatus.message && (
                  <div className={`demo-form__status ${submissionStatus.loading ? "loading" : "success"}`}>
                    {submissionStatus.message}
                  </div>
                )}

                {/* Row 1 */}
                <div className="demo-form__row">
                  <label htmlFor="name" className="demo-form__label">
                    Name*
                  </label>
                  <div className="demo-form__input-wrapper">
                    <input
                      type="text"
                      id="demo-form-name-input"
                      name="name"
                      className={`demo-form__input ${errors.name ? "error" : formData.name ? "success" : ""}`}
                      placeholder="Alex Green"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={submissionStatus.loading}
                    />
                    {formData.name && !errors.name && (
                      <span className="demo-form__icon">
                        <img src="/images/success-mark.svg" alt="success mark" />
                      </span>
                    )}
                  </div>
                  {errors.name && (
                    <span className="demo-form__error">
                      <img src="/images/vulnerability-filled-icon.svg" alt="vulnerability" />
                      <span>{errors.name}</span>
                    </span>
                  )}
                </div>

                {/* Row 2 */}
                <div className="demo-form__row">
                  <label htmlFor="company" className="demo-form__label">
                    Company name*
                  </label>
                  <div className="demo-form__input-wrapper">
                    <input
                      type="text"
                      id="demo-form-company-input"
                      name="company"
                      className={`demo-form__input ${errors.company ? "error" : formData.company ? "success" : ""}`}
                      placeholder="Acme Inc"
                      value={formData.company}
                      onChange={handleInputChange}
                      disabled={submissionStatus.loading}
                    />
                    {formData.company && !errors.company && (
                      <span className="demo-form__icon">
                        <img src="/images/success-mark.svg" alt="success mark" />
                      </span>
                    )}
                  </div>
                  {errors.company && (
                    <span className="demo-form__error">
                      <img src="/images/vulnerability-filled-icon.svg" alt="vulnerability" />
                      <span>{errors.company}</span>
                    </span>
                  )}
                </div>

                {/* Row 3 */}
                <div className="demo-form__row">
                  <label htmlFor="email" className="demo-form__label">
                    Business email*
                  </label>
                  <div className="demo-form__input-wrapper">
                    <input
                      type="email"
                      id="demo-form-email-input"
                      name="email"
                      className={`demo-form__input ${errors.email ? "error" : formData.email ? "success" : ""}`}
                      placeholder="alex@acme.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={submissionStatus.loading}
                    />
                    {formData.email && !errors.email && (
                      <span className="demo-form__icon">
                        <img src="/images/success-mark.svg" alt="success mark" />
                      </span>
                    )}
                  </div>
                  {errors.email && (
                    <span className="demo-form__error">
                      <img src="/images/vulnerability-filled-icon.svg" alt="vulnerability" />
                      <span>{errors.email}</span>
                    </span>
                  )}
                </div>

                {/* Phone Block */}
                <div className="demo-form__row">
                  <label htmlFor="phone" className="demo-form__label">
                    Phone Number
                  </label>
                  <div className="demo-form__phone-wrapper">
                    <div className="demo-form__select-wrapper">
                      <img
                        className="demo-form__select-country-icon"
                        src="/images/countries-select/1.svg"
                        alt="unknown country"
                      />
                      <img className="demo-form__select-arrow" src="/images/arrow-down.svg" alt="arrow-down" />

                      <select
                        id="country-code"
                        name="countryCode"
                        className="demo-form__select"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        disabled={submissionStatus.loading}
                      >
                        <option value="+971">+971</option>
                        <option value="+44">+44</option>
                        <option value="+91">+91</option>
                      </select>
                    </div>
                    <div className="demo-form__input-wrapper">
                      <input
                        type="tel"
                        id="demo-form-phone-input"
                        name="phone"
                        className={`demo-form__input ${errors.phone ? "error" : formData.phone ? "success" : ""}`}
                        placeholder="569913445"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={submissionStatus.loading}
                      />
                      {formData.phone && !errors.phone && (
                        <span className="demo-form__icon">
                          <img src="/images/success-mark.svg" alt="success mark" />
                        </span>
                      )}
                    </div>
                  </div>
                  {errors.phone && (
                    <span className="demo-form__error">
                      <img src="/images/vulnerability-filled-icon.svg" alt="vulnerability" />
                      <span>{errors.phone}</span>
                    </span>
                  )}
                </div>

                {/* Textarea */}
                <div className="demo-form__row">
                  <label htmlFor="message" className="demo-form__label">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="demo-form__textarea"
                    placeholder="Your message here"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={submissionStatus.loading}
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="demo-form__row">
                  <button
                    type="submit"
                    className={`demo-form__button btn btn-primary ${submissionStatus.loading ? "loading" : ""}`}
                    disabled={submissionStatus.loading}
                  >
                    {submissionStatus.loading ? "Sending..." : "Request My Free Demo"}
                  </button>
                </div>

                {/* Honeypot field to prevent spam */}
                <div style={{ display: "none" }}>
                  <input type="text" name="botcheck" />
                </div>
              </form>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={400} className="demo-benefits">
              <div className="demo-benefits__image resonance-image glass-border-wrapper">
                <img src="/images/team-success.svg" alt="Demo Benefits" />
              </div>

              <div className="demo-benefits__content">
                <h3 className="demo-benefits__title">Why Try spiderSilk?</h3>
                <h4 className="demo-benefits__subtitle">Here's what to expect from your free demo:</h4>

                <ul className="demo-benefits__list">
                  <li className="demo-benefits__list-item">
                    <div className="demo-benefits__list-item-icon complete-mark"></div>
                    <span>Live walkthrough of the Resonance platform</span>
                  </li>
                  <li className="demo-benefits__list-item">
                    <div className="demo-benefits__list-item-icon complete-mark"></div>
                    <span>Overview of how we find hidden exposures others miss</span>
                  </li>
                  <li className="demo-benefits__list-item">
                    <div className="demo-benefits__list-item-icon complete-mark"></div>
                    <span>Customized view based on your industry and needs</span>
                  </li>
                  <li className="demo-benefits__list-item">
                    <div className="demo-benefits__list-item-icon complete-mark"></div>
                    <span>Clear pricing and implementation timeline</span>
                  </li>
                  <li className="demo-benefits__list-item">
                    <div className="demo-benefits__list-item-icon complete-mark"></div>
                    <span>All your questions answered by security experts</span>
                  </li>
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  );
};

export default BookDemoPage;
